import React, { useCallback } from 'react';
import { Collapse, Space } from 'antd';
import useNativeSortableList from '../../../hooks/useNativeSortableList';
import SortableDragHandle from '../../common/SortableDragHandle';

const COLLAPSE_BODY_STYLE = { padding: 0 };
const COLLAPSE_HEADER_STYLE = { alignItems: 'center' };

/**
 * Collapse panels for each dashboard section with drag-and-drop reorder on the grip handle.
 */
export default function SortableDashboardSections({
    dashboards = [],
    getDashboardId,
    expandedKeys = [],
    onExpandedKeysChange,
    renderLabel,
    renderExtra,
    renderPanelBody,
    onReorder,
    reorderDisabled = false,
}) {
    const {
        dragIndex,
        containerProps,
        setItemRef,
        getHandleProps,
        insertionIndicator,
    } = useNativeSortableList({
        orientation: 'vertical',
        disabled: reorderDisabled,
        onReorder,
    });

    const toggleExpanded = useCallback((id, keys) => {
        const keyList = Array.isArray(keys) ? keys : (keys ? [keys] : []);
        const isOpen = keyList.includes(id);
        onExpandedKeysChange?.(prev => {
            const list = prev ?? [];
            if (isOpen) return list.includes(id) ? list : [...list, id];
            return list.filter(k => k !== id);
        });
    }, [onExpandedKeysChange]);

    if (dashboards.length === 0) return null;

    return (
        <div {...containerProps} style={{ position: 'relative', width: '100%' }}>
            {insertionIndicator && <div aria-hidden {...insertionIndicator} />}
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {dashboards.map((db, index) => {
                    const id = getDashboardId(db);

                    return (
                        <div
                            key={id}
                            ref={setItemRef(index)}
                            style={{
                                width: '100%',
                                opacity: dragIndex === index ? 0.45 : 1,
                            }}
                        >
                            <Collapse
                                bordered={false}
                                destroyOnHidden
                                activeKey={expandedKeys.includes(id) ? [id] : []}
                                onChange={keys => toggleExpanded(id, keys)}
                                items={[{
                                    key: id,
                                    label: (
                                        <Space size={8}>
                                            <SortableDragHandle
                                                ariaLabel="Drag to reorder section"
                                                className="dashboard-section-drag-handle"
                                                disabled={reorderDisabled}
                                                iconSize={14}
                                                {...getHandleProps(index)}
                                            />
                                            <span style={{ flex: 1 }}>{renderLabel(db, index)}</span>
                                        </Space>
                                    ),
                                    extra: renderExtra?.(db, index),
                                    style: COLLAPSE_HEADER_STYLE,
                                    styles: { body: COLLAPSE_BODY_STYLE },
                                    children: renderPanelBody(db, index),
                                }]}
                            />
                        </div>
                    );
                })}
            </Space>
        </div>
    );
}
